/* I-2 · the print file, B's half (campaign-system review § I row I-2, word 4; the I-2 vet 08/10 « For B »; the
   « prolonger » vet: a support without a link is a bug to show, never a code to print). Contract 06 §6.4: the app makes
   the print file, the API serves none; the server PDF (pdf-lib) waits for a print shop's ask. One support's sheet at its
   model's size, with its own QR: the lib's `supportUrl(token)`, drawn by @brand/qr (Technical's renderer) at the side
   the brand's README gives, quiet zone included. « Télécharger le PDF » opens the browser's print window: @page at the
   model's size, margin 0, and only the sheet on the page (the app around it does not print); while it prints, the
   document's title, which names the file, is the support's and its establishment's. Version 19's words (`A.pdf`): its
   sentence, and its Format / QR / Ouvre lines. Printing has no event: nothing is ticked, nothing is recorded (MA-8
   ruling, default 3).
   WF-086 (v3.3 migration, pack supports-a): « Télécharger le PDF » first opens « Vérifier le PDF réel » — « Ouvrir le
   PDF » is the same print window, a self-check line, the candour on the local proof, and « Approuver le visuel », the
   merchant's own declaration (lib/readiness « artwork reviewed », kept in this browser until the API has the field).
   No stamp on the sheet, no toast on the download; the approval gates nothing. */
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { Button, Card, Checkbox, Icon, KV, Modal, Note, Pill, useToast } from '@/ui'
import { supportUrl } from '@/lib/api'
import { SUPPORT_MODELS, defaultSupportDesign, type CampaignLite, type SupportModel, type SupportRef } from '@/lib/campaign'
import { declareReadiness, readiness } from '@/lib/readiness'
import { supportPlace } from '@/lib/organisation'
import { SupportPreview, qrMarkup } from './Designer'

/** A support's model: its design's, else the one its genre word names (« grand format » without a design: the A3). */
const MODEL_OF_KIND: Record<string, SupportModel> = { chevalet: 'chevalet-a6', sticker: 'sticker-50', affiche: 'affiche-a4', 'grand format': 'affiche-a3', 'écran': 'ecran-portrait' }
export const modelOf = (s: SupportRef): SupportModel | null => s.design?.model ?? MODEL_OF_KIND[s.kind] ?? null

/** Is this a support to print (a model with a printed QR side)? A member's personal QR, a link, a screen are not. */
export const printable = (s: SupportRef | undefined): boolean => {
  const m = s && s.kind !== 'personnel' ? modelOf(s) : null
  return !!m && !!SUPPORT_MODELS.find(x => x.id === m)?.qrMm
}

/** « à la taille du chevalet » (Version 19), and the same for the other models */
const SIZE_OF: Partial<Record<SupportModel, string>> = { 'chevalet-a6': 'du chevalet', 'sticker-50': 'du sticker', 'affiche-a4': 'de l’affiche', 'affiche-a3': 'de l’affiche', 'vitrine-150': 'du rond de vitrine' }
/** the Format line: Version 19's for the chevalet, the same shape for the others */
const FORMAT_OF: Partial<Record<SupportModel, string>> = { 'chevalet-a6': 'A6, 105 × 148 mm, à plier', 'sticker-50': 'Sticker rond, Ø 50 mm', 'affiche-a4': 'Affiche A4, 210 × 297 mm', 'affiche-a3': 'Affiche A3, 297 × 420 mm', 'vitrine-150': 'Rond de vitrine, Ø 150 mm' }
/** the closing screen's row 1 (« Imprimer votre QR »): merchant Version 8's line for the chevalet, the same for the others */
export const READY_LINE: Partial<Record<SupportModel, string>> = { 'chevalet-a6': 'Un chevalet A6 pour le comptoir, prêt à imprimer.', 'sticker-50': 'Un sticker rond de 50 mm, prêt à imprimer.', 'affiche-a4': 'Une affiche A4, prête à imprimer.', 'affiche-a3': 'Une affiche A3, prête à imprimer.', 'vitrine-150': 'Un rond de vitrine de 150 mm, prêt à imprimer.' }
const PX_PER_MM = 96 / 25.4

export default function Imprimer({ support: s, place, opens, served = null, onBack, onDesign, onApproved }: { support: SupportRef; place: string; opens: string; served?: CampaignLite | null; onBack: () => void; onDesign?: () => void; onApproved?: () => void }) {
  const toast = useToast()
  /* point 6 · the emplacement as the list names it, else the free text said at creation */
  const where = (supportPlace(s.id, s as { placeId?: string | null })?.name ?? s.place)?.trim() || null
  const [proofOpen, setProofOpen] = useState(false)
  const [checked, setChecked] = useState(false)
  const artwork = readiness(s, served).find(x => x.key === 'artwork')!
  const model = modelOf(s)
  const spec = SUPPORT_MODELS.find(m => m.id === model)
  const back = <p className="f-help" style={{ margin: 0 }}><a href="#" onClick={e => { e.preventDefault(); onBack() }}>‹ {s.name}</a></p>
  if (!spec?.qrMm || s.kind === 'personnel') return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 680 }} data-testid="imprimer">
      {back}
      <Note tone="candour">Ce support ne s’imprime pas&nbsp;: son lien est dans sa page.</Note>
    </div>
  )
  const url = s.token ? supportUrl(s.token) : undefined
  /* a support with no link draws no QR and offers no file: the bug shows */
  if (!url || !qrMarkup(url)) return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 680 }} data-testid="imprimer">
      {back}
      <Note tone="crit" data-testid="imprimer-erreur">Ce support n’a pas de lien&nbsp;: rien à imprimer. Rechargez la page.</Note>
    </div>
  )
  const design = s.design ?? defaultSupportDesign(spec.id)
  const fileName = `${s.name} · ${place}`
  const print = () => {
    const before = document.title
    const restore = () => { document.title = before; window.removeEventListener('afterprint', restore) }
    window.addEventListener('afterprint', restore)
    document.title = fileName
    window.print()
  }
  const sheet = (
    <div className="bmr-print" data-testid="feuille">
      <style>{`.bmr-print { display: none }
@page { size: ${spec.w}mm ${spec.h}mm; margin: 0 }
@media print {
  html, body { margin: 0 !important; padding: 0 !important; background: #fff !important }
  body > *:not(.bmr-print) { display: none !important }
  .bmr-print { display: block !important; width: ${spec.w}mm; height: ${spec.h}mm; overflow: hidden }
  .bmr-print * { -webkit-print-color-adjust: exact; print-color-adjust: exact }
}`}</style>
      <SupportPreview design={design} place={place} width={spec.w * PX_PER_MM} url={url} print />
    </div>
  )
  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 760 }} data-testid="imprimer">
      {back}
      <h2 className="t-page" style={{ margin: 0 }}>Imprimer — {s.name}</h2>
      <p className="t-body" style={{ margin: 0 }}>Le fichier d’impression, à la taille {SIZE_OF[spec.id]}, avec le vrai QR de ce support. Il ne change jamais&nbsp;: si vous changez de campagne, inutile de réimprimer.</p>
      <div style={{ display: 'grid', gap: 18, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', alignItems: 'start' }}>
        <div style={{ display: 'grid', justifyItems: 'center' }} data-testid="apercu">
          <SupportPreview design={design} place={place} width={spec.round ? 280 : 300} url={url} />
        </div>
        <Card>
          <KV rows={[
            ['Format', FORMAT_OF[spec.id] ?? `${spec.label}, ${spec.size}`],
            /* I-3 · « Où : Table 7 » (scenario 4): the place said at creation, when there is one */
            ...(where ? [['Où', where] as [string, string]] : []),
            ['QR', <span key="q" data-testid="imprimer-lien" style={{ fontFamily: "'IBM Plex Mono', monospace", wordBreak: 'break-all' }}>{url.replace(/^https?:\/\//, '')}</span>],
            ['Ouvre', opens],
          ]} />
          <div style={{ display: 'grid', gap: 8, marginTop: 14 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button variant="primary" onClick={() => { setChecked(false); setProofOpen(true) }}><Icon name="print" /> Télécharger le PDF</Button>
              <Button variant="quiet" onClick={onBack}>Retour</Button>
            </div>
            <span className="f-help" style={{ margin: 0 }}>«&nbsp;Vérifier le PDF réel&nbsp;» ouvre d’abord l’épreuve&nbsp;; dans la fenêtre d’impression, choisissez « Enregistrer au format PDF », ou votre imprimante.</span>
            <span data-testid="visuel-etat"><Pill tone={artwork.done ? 'ok' : 'warn'}>{artwork.label}</Pill> <span className="f-help">{artwork.hint}</span></span>
          </div>
        </Card>
      </div>
      {createPortal(sheet, document.body)}
      <Modal open={proofOpen} title="Vérifier le PDF réel" onClose={() => setProofOpen(false)}
        actions={<>
          <Button variant="quiet" onClick={() => { setProofOpen(false); (onDesign ?? onBack)() }}>Revenir au design</Button>
          <Button variant="primary" disabled={!checked} onClick={() => { declareReadiness(s.id, 'artwork', true); setProofOpen(false); toast({ message: `✓ Visuel approuvé — « ${s.name} ». Votre déclaration, rien d’autre : le fichier reste l’épreuve à relire avant un tirage.` }); onApproved?.() }}>Approuver le visuel</Button>
        </>}>
        <div style={{ display: 'grid', gap: 10 }} data-testid="verifier-pdf">
          <p className="t-body" style={{ margin: 0 }}>La vérification ouvre le PDF réel. Relisez le texte, la taille et la mise en page.</p>
          <div><Button onClick={print}><Icon name="print" /> Ouvrir le PDF</Button></div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5 }}><Checkbox checked={checked} onChange={e => setChecked(e.target.checked)} /> J’ai relu le fichier, le format, le texte français et le QR</label>
          <Note tone="candour">Épreuve locale. Une adaptation au gabarit exact de l’imprimeur et un test du tirage final restent nécessaires.</Note>
        </div>
      </Modal>
    </div>
  )
}
